import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import slugify from '@sindresorhus/slugify';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/system';
import CompanyIcon from 'assets/icons/mdi_company.svg?react';
import CashIcon from 'assets/icons/cash-white.svg?react';
import AddressIcon from 'assets/icons/location-white.svg?react';
import LinkIcon from 'assets/icons/icon-gg_arrow-up-r.svg?react';
import { DAILY } from 'variables/main';
import { triggerDailyQuest } from 'actions/gamification';
import { getLocalizedPath } from 'helpers/getLocale';

const COLUMNS_LIMIT = 8;

const Title = styled(Typography)(({ theme, darkTheme }) => ({
  padding: 16,
  textAlign: darkTheme ? 'left' : 'center',
  color: darkTheme ? theme.palette.white.main : theme.palette.black.main,
  fontSize: 24,
  paddingLeft: darkTheme ? 0 : 16,
  [theme.breakpoints.down('md')]: {
    lineHeight: '34px'
  },
  [theme.breakpoints.down('sm')]: {
    textAlign: 'left',
    paddingLeft: 0
  }
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
  color: theme.palette.white.main,
  borderRadius: 8,
  borderBottom: `1px solid ${theme.palette.white.main}`,
  padding: 0,
  paddingBottom: 5,
  fontSize: 16,
  margin: '0 auto',
  width: 140,
  display: 'block',
  '&:hover': {
    color: theme.palette.white.main,
    backgroundColor: 'transparent'
  }
}));

const Vacancies = styled('div')(({ theme }) => ({
  gap: 10,
  display: 'grid',
  gridTemplateColumns: `repeat(${COLUMNS_LIMIT / 2}, 1fr)`,
  gridAutoRows: 'auto',
  marginBottom: 20,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr'
  }
}));

const Vacancy = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  borderRadius: 15,
  cursor: 'pointer',
  padding: '16px 22px',
  transition: 'background-color 0.2s',
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark
  }
}));

const VacancyTitle = styled(Typography)(() => ({
  fontSize: 16,
  lineHeight: 1.25,
  marginBottom: 10
}));

const VacancyText = styled(Typography)(() => ({
  fontSize: 14,
  display: 'flex',
  alignItems: 'flex-start',
  lineHeight: '22px',
  gap: 10,
  marginBottom: 5,
  '& svg': {
    width: 24,
    height: 24
  },
  '& span': {
    maxWidth: 200
  }
}));

const Wrapper = styled('div')(({ theme, slides }) => ({
  marginBottom: 65,
  [theme.breakpoints.down('sm')]: {
    marginBottom: slides === 'true' ? 30 : 65
  }
}));

const LinkIconStyles = styled(LinkIcon)(() => ({
  position: 'absolute',
  bottom: 12,
  right: 12,
  width: 24,
  height: 24
}));

const VacanciesComponent = ({
  vacanciesList,
  darkTheme,
  slides,
  customHeadline,
  title = '',
  pagination = true,
  currentId = null,
  emptyState
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const t = useTranslate('Profession');

  const handleRedirectVacancies = React.useCallback(
    (vacancy) => {
      dispatch(triggerDailyQuest(DAILY.goToJobVacancy));
      window.open(
        getLocalizedPath(`/vacancies/${slugify(vacancy.title || 'vacancy')}/${vacancy._id}`),
        '_blank'
      );
    },
    [dispatch]
  );

  // Drop entries missing _id — the click handler builds /vacancies/:slug/:id
  // from vacancy._id, so a missing id produces a literal "undefined" segment
  // and a 404 when followed.
  const vacancies = (vacanciesList || []).filter((v) => v && v._id);

  // Hide component if no vacancies — except in slide mode where the caller
  // supplies a beautified emptyState slot. The main page still collapses
  // the section silently to avoid a hollow placeholder mid-scroll.
  if (vacancies.length === 0) {
    if (emptyState) return <Wrapper slides={`${slides}`}>{emptyState}</Wrapper>;
    return null;
  }

  return (
    <Wrapper slides={`${slides}`}>
      <Title variant={'h2'} darkTheme={darkTheme}>
        {customHeadline || t('Vacancies')}
      </Title>

      <Vacancies>
        {vacancies.map((vacancy, index) => {
          if (index >= COLUMNS_LIMIT) return null;

          return (
            <Vacancy key={vacancy?._id} onClick={() => handleRedirectVacancies(vacancy)}>
              <VacancyTitle variant="h3">{vacancy?.title || '-'}</VacancyTitle>
              <VacancyText variant="body1">
                <CompanyIcon />
                <span>{vacancy?.company?.name || vacancy?.company || '-'}</span>
              </VacancyText>
              <VacancyText variant="body1">
                <AddressIcon />
                <span>
                  {vacancy?.salary ? vacancy?.salary + ' ' + t('UAH') : t('SalaryNotSpecified')}
                </span>
              </VacancyText>
              <VacancyText variant="body1">
                <CashIcon />
                <span>{vacancy?.location || '-'}</span>
              </VacancyText>
              <LinkIconStyles />
            </Vacancy>
          );
        })}
      </Vacancies>

      {vacancies.length && pagination ? (
        <ButtonStyled
          aria-label={t('ShowMoreVacancies')}
          onClick={() =>
            navigate(getLocalizedPath(`/vacancies?title=${title}&filters[companies]=${currentId}`))
          }>
          {t('ShowMoreVacancies')}
        </ButtonStyled>
      ) : null}
    </Wrapper>
  );
};

export default VacanciesComponent;
